import React, { useState } from "react";
import { PolicyType } from "src/constants/policyType";
import styled from "styled-components";
import { Dialog, Divider } from "@mui/material";
import Text from "./Text";
import Button from "./Button";
import MeLogo from "src/img/new/me_license_logo.svg";
import OpenLinkIcon from "src/img/web link.svg";
import Checkbox from "src/components/Checkbox";
import { UserKeys } from "src/constants/userDetails";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import { Toast } from "src/components";
import { getAppDetails } from "src/config/appVariants";

const Heading = styled(Text)`
    font-size: 1.25rem;
    font-weight: 700;
    margin: 2rem 0 1rem 0;
`;

const LoadingButtonPlaceholder = styled(Button)`
    color: white;
    background: white;
    border: 1px solid black;
    cursor: $unset;
    border-radius: 7px !important;
    width: 8.75rem;
    height: 2.3125rem;
    line-height: 50%;
    margin: 0 0.5rem;
`;

const StyledButton = styled(Button)`
    color: ${p => p.foreground};
    background: ${p => p.fill} 0 0 no-repeat padding-box;
    border: 1px solid ${p => p.outline};
    cursor: ${p => p.isActive ? "pointer" : "unset"};
    border-radius: 7px !important;
    width: 8.75rem;
    height: 2.3125rem;
    line-height: 50%;
    margin: 0 0.5rem;
`;

function PolicyLinkButton({ appVariant, label, policyType }) {
    const app = getAppDetails(appVariant);
    let url = "";
    if (policyType === PolicyType.termsAndConditions) {
        url = app.termsUrl;
    } else if (policyType === PolicyType.privacyPolicy) {
        url = app.privacyUrl;
    }
    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
                color: "black",
                textDecoration: "none",
                fontWeight: 500,
                margin: "0 0.5rem"
            }}
        >
            <div style={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                border: "1px solid #D8DEEC",
                borderRadius: "12px",
                boxShadow: "0px 3px 6px #00000029",
                width: "250px",
                height: "50px"
            }}>
                {label}
                <img src={OpenLinkIcon} width="10" height="10" style={{ position: "absolute", top: "8px", right: "12px" }} />
            </div>
        </a>
    );
}

function AcceptPoliciesModal({ appVariant, policyToAccept, hideModal }) {

    const [checkboxChecked, setCheckboxChecked] = useState(false);
    const [requestIsInFlight, setRequestIsInFlight] = useState(false);

    const toggleCheckbox = () => {
        setCheckboxChecked(!checkboxChecked);
    };

    const submitAcceptance = async () => {
        if (!checkboxChecked) {
            return;
        }
        const token = localStorage.getItem(UserKeys.TOKEN);
        setRequestIsInFlight(true);
        try {
            await axios.post(`${API_URL}/acceptPolicies`, {}, {
                headers: {
                    Authorization: "Bearer " + token
                }
            });
            window.location.href = "/active"; // Reload everything (API requests will have failed)
        } catch (error) {
            console.error(error);
            Toast.error("An issue occurred accepting policies.");
        } finally {
            setRequestIsInFlight(false);
        }
    };

    let acceptTermsRequired = false;
    let acceptPrivacyRequired = false;
    switch (policyToAccept) {
    case PolicyType.termsAndConditions:
        acceptTermsRequired = true;
        break;
    case PolicyType.privacyPolicy:
        acceptPrivacyRequired = true;
        break;
    case PolicyType.termsAndPrivacy:
        acceptTermsRequired = true;
        acceptPrivacyRequired = true;
    }

    let longPoliciesName = "";
    let shortPoliciesName = "";
    let pluralPolicy = false;
    if (acceptTermsRequired && acceptPrivacyRequired) {
        longPoliciesName = "Terms and Conditions and Privacy Policy";
        shortPoliciesName = "Terms & Policy";
        pluralPolicy = true;
    } else if (acceptTermsRequired) {
        longPoliciesName = "Terms and Conditions";
        shortPoliciesName = "Terms";
    } else if (acceptPrivacyRequired) {
        longPoliciesName = "Privacy Policy";
        shortPoliciesName = "Policy";
    }

    return (
        <Dialog
            open={true}
            fullWidth={false}
            maxWidth={false}
            disableEscapeKeyDown
            scroll="body"
            PaperProps={{
                style: {
                    border: "1px solid #D8DEEC",
                    width: "46rem",
                    textAlign: "center",
                    padding: "2rem 0"
                },
            }}
        >
            <img src={MeLogo} width="110" height="34" />
            <Divider style={{ margin: "2rem 0 0" }} />
            <Heading>Updated {longPoliciesName}</Heading>
            <Text maxWidth="540px" margin="0 auto 1rem">
                Our {longPoliciesName} {pluralPolicy ? "have" : "has"} been recently updated.
                To continue using our app, please review and agree to our updated terms.
            </Text>
            <div style={{ display: "flex", justifyContent: "center" }}>
                { acceptTermsRequired &&
                    <PolicyLinkButton
                        appVariant={appVariant}
                        label="Terms & Conditions"
                        policyType={PolicyType.termsAndConditions}
                    />
                }
                { acceptPrivacyRequired &&
                    <PolicyLinkButton
                        appVariant={appVariant}
                        label="Privacy Policy"
                        policyType={PolicyType.privacyPolicy}
                    />
                }
            </div>
            <Text weight={600}>
                I have read and agree to the {shortPoliciesName}.
            </Text>
            <label>
                <Checkbox
                    color="#36be24"
                    height="32px"
                    width="32px"
                    padding="7px"
                    radius="10px"
                    value={true}
                    checked={checkboxChecked}
                    onChange={toggleCheckbox}
                />
            </label>
            <Divider style={{ margin: "1rem 0 1.5rem" }} />
            <div style={{ display: "flex", justifyContent: "center" }}>
                <StyledButton
                    onClick={hideModal}
                    fill="white"
                    outline="black"
                    foreground="black"
                    isActive={true}
                >
                    Cancel
                </StyledButton>
                { requestIsInFlight &&
                    <LoadingButtonPlaceholder />
                }
                { !requestIsInFlight &&
                    <StyledButton
                        onClick={submitAcceptance}
                        fill={checkboxChecked ? "black" : "#C4C4C4"}
                        outline={checkboxChecked ? "black" : "#C4C4C4"}
                        foreground="white"
                        isActive={checkboxChecked}
                    >
                        Continue
                    </StyledButton>
                }
            </div>
        </Dialog>
    );
}

export default AcceptPoliciesModal;
